import type { BusinessType } from "@/components/screens/ManageAccounts/LinkedAccounts/Types/LinkedAccountsTypes";
import type { ID } from "@/types/api/api";
import type { OrganisationRelationship } from "@/types/api/contracts/organisations";
import type { FeatureFlag } from "@/types/auth/FeatureFlags";

import { createContext, useState, type ReactNode } from "react";

export interface IOrganisationInfo {
  address1: string;
  town: string;
  postcode: string;
  contact_email: string;
  contact_number: string;
  account_name: string;
  vat_number: string;
  pin_protected_permissions: Array<string>;
  country_code: string;
  telephone: string;
  business_type: BusinessType;
  companies_house_number: string;
}

interface VenueSpecificContextType {
  setSelectedVenue: (venue: ID) => void;
  selectedVenue: ID;
  venues: {
    id: ID;
    name: string;
  }[];
  isVenueSpecific: boolean;
  timezone: string;
  currency: string;
  organisation_id: ID;
  organisation_identifier: string;
  organisation_info: IOrganisationInfo;
  getFeatureFlags: () => Record<FeatureFlag, boolean>;
}

const VenueContext = createContext<VenueSpecificContextType>(
  {} as VenueSpecificContextType,
);

export const VenueSpecificProvider: React.FC<{
  children: ReactNode;
  lastSelectedVenue?: number | string;
  isVenueSpecific: boolean;
  organisationRelationships: OrganisationRelationship[];
  timezone: string;
  currency: string;
  organisation_id: ID;
  organisation_identifier: string;
  organisation_info: IOrganisationInfo;
  featureFlags: Record<FeatureFlag, boolean>;
}> = ({
  children,
  lastSelectedVenue,
  isVenueSpecific,
  organisationRelationships,
  timezone,
  currency,
  organisation_id,
  organisation_identifier,
  organisation_info,
  featureFlags,
}) => {
  const venues = (organisationRelationships || []).map((relationship) => ({
    id: relationship.restaurant.id,
    name: relationship.restaurant.name,
  }));

  const [selectedVenue, setSelectedVenue] = useState(
    lastSelectedVenue || venues[0]?.id,
  );

  const getFeatureFlags = () => {
    return featureFlags;
  };

  return (
    <VenueContext.Provider
      value={{
        selectedVenue,
        setSelectedVenue,
        venues,
        isVenueSpecific,
        timezone,
        currency,
        organisation_id,
        organisation_identifier,
        organisation_info,
        getFeatureFlags,
      }}
    >
      {children}
    </VenueContext.Provider>
  );
};

export const VenueConsumer = VenueContext.Consumer;

export default VenueContext;
